import { Dispatch } from "redux";
import { ApiService } from "../../../services/apiService";
import { routes } from "../../../services/paths";
import { appConstants } from "../../action-constants/inapp.constants";
import { handleRequestErrors } from "../../../shared/utils";
import {
  buildCustomerExportQuery,
  CUSTOMER_EXPORT_BATCH_SIZE,
  CustomerExportRequest,
  downloadCustomerCsv,
  getCustomerExportFileName,
  getCustomerExportHeaderRow,
  mapCustomerToExportRow,
} from "../../../views/app/customers/utils/customerExportUtils";

const axios = require("axios");

let activeCancelSource: any = null;

const getCustomerPageData = (response: any) => {
  const data = response?.data?.data || response?.data || {};

  return {
    results: data?.results || [],
    totalNumberOfRecords:
      data?.totalNumberOfRecords || data?.totalCount || data?.totalRecords || 0,
  };
};

const isAuthenticated = () => {
  const auth = localStorage.getItem("mami-Super-XAuth");
  return Boolean(auth && auth !== "{}");
};

const yieldToBrowser = () =>
  new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });

export const cancelCustomerCsvExportRequest = () => {
  if (activeCancelSource) {
    activeCancelSource.cancel("Customer export cancelled.");
    activeCancelSource = null;
  }
};

const StartCustomerCsvExport = (exportRequest: CustomerExportRequest) => {
  return async (dispatch: Dispatch) => {
    cancelCustomerCsvExportRequest();

    activeCancelSource = axios.CancelToken.source();
    const csvChunks = [getCustomerExportHeaderRow()];
    let fetchedCount = 0;
    let totalCount = 0;
    let currentPage = 1;

    dispatch({
      type: appConstants.EXPORT_CUSTOMERS_CSV_START,
      payload: {
        exportRequest,
        batchSize: CUSTOMER_EXPORT_BATCH_SIZE,
      },
    });

    try {
      while (isAuthenticated()) {
        const query = buildCustomerExportQuery({
          ...exportRequest,
          page: currentPage,
          size: CUSTOMER_EXPORT_BATCH_SIZE,
        });

        const response: any = await ApiService.request({
          url: `${routes.GET_CUSTOMERS}${query}`,
          http_method: "GET",
          cancelToken: activeCancelSource?.token,
        });

        const { results, totalNumberOfRecords } = getCustomerPageData(response);
        totalCount = totalNumberOfRecords || totalCount || results.length;

        if (!results.length) break;

        csvChunks.push(results.map(mapCustomerToExportRow).join("\n"));
        fetchedCount += results.length;

        dispatch({
          type: appConstants.EXPORT_CUSTOMERS_CSV_PROGRESS,
          payload: {
            fetchedCount,
            totalCount,
            currentPage,
            percent: totalCount
              ? Math.min(100, Math.round((fetchedCount / totalCount) * 100))
              : 0,
          },
        });

        if (totalCount && fetchedCount >= totalCount) break;

        currentPage += 1;
        await yieldToBrowser();
      }

      if (!isAuthenticated()) {
        cancelCustomerCsvExportRequest();
        dispatch({
          type: appConstants.EXPORT_CUSTOMERS_CSV_CANCEL,
          payload: { message: "Customer export stopped because you logged out." },
        });
        return;
      }

      const fileName = getCustomerExportFileName();
      downloadCustomerCsv(csvChunks.join("\n"), fileName);

      dispatch({
        type: appConstants.EXPORT_CUSTOMERS_CSV_SUCCESS,
        payload: {
          fetchedCount,
          totalCount: totalCount || fetchedCount,
          fileName,
        },
      });
    } catch (error: any) {
      if (axios.isCancel(error)) {
        dispatch({
          type: appConstants.EXPORT_CUSTOMERS_CSV_CANCEL,
          payload: { message: "Customer export cancelled." },
        });
        return;
      }

      dispatch({
        type: appConstants.EXPORT_CUSTOMERS_CSV_FAILURE,
        payload: {
          error: handleRequestErrors(error),
          exportRequest,
          fetchedCount,
          totalCount,
        },
      });
    } finally {
      activeCancelSource = null;
    }
  };
};

const CancelCustomerCsvExport = () => {
  cancelCustomerCsvExportRequest();
  return {
    type: appConstants.EXPORT_CUSTOMERS_CSV_CANCEL,
    payload: { message: "Customer export cancelled." },
  };
};

const ResetCustomerCsvExport = () => ({
  type: appConstants.EXPORT_CUSTOMERS_CSV_RESET,
});

export const customerExportActions = {
  StartCustomerCsvExport,
  CancelCustomerCsvExport,
  ResetCustomerCsvExport,
};
